import { Box, Button, MenuItem, TextField, Typography } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import { MainMenuToolbar } from "../components/MainMenuToolbar";
import type { ApiError, StaticEquipmentForm } from "../types/models";

const equipmentTypes = ["winch", "mooring_bit"];
const directions = ["FWD", "AFT"];

type StaticEquipmentPartialProps = {
  title: string;
  backLink: string;
  submitButtonTitle: string;
  editedEquipment: StaticEquipmentForm | undefined;
  error: ApiError | undefined;
  setEditedEquipment: (value: StaticEquipmentForm) => void;
  onSubmitClick: () => void;
};

export const StaticEquipmentPartial = ({
  title,
  backLink,
  submitButtonTitle,
  editedEquipment,
  error,
  setEditedEquipment,
  onSubmitClick
}: StaticEquipmentPartialProps) => {
  const globalError = error?.message?.message;

  const setNumber = (
    key: "position_on_deck_x" | "position_on_deck_y",
    value: string
  ) => {
    if (value === "") {
      setEditedEquipment({ ...editedEquipment, [key]: "" });
      return;
    }

    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      setEditedEquipment({ ...editedEquipment, [key]: parsed });
    }
  };

  return (
    <div>
      <MainMenuToolbar title={title} backLink={backLink} />
      <Container maxWidth="sm">
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <TextField
            id="input-type"
            label="Type"
            margin="normal"
            select
            value={editedEquipment?.type || ""}
            onChange={(event) => {
              setEditedEquipment({ ...editedEquipment, type: event.target.value });
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          >
            {equipmentTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="input-direction"
            label="Direction"
            margin="normal"
            select
            value={editedEquipment?.direction || ""}
            onChange={(event) => {
              setEditedEquipment({ ...editedEquipment, direction: event.target.value });
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          >
            {directions.map((direction) => (
              <MenuItem key={direction} value={direction}>
                {direction}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="input-station-name"
            label="Station name"
            margin="normal"
            value={editedEquipment?.station_name || ""}
            onChange={(event) => {
              setEditedEquipment({
                ...editedEquipment,
                station_name: event.target.value,
              });
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          />

          <TextField
            id="input-position-x"
            label="Deck position X"
            margin="normal"
            value={editedEquipment?.position_on_deck_x ?? ""}
            onChange={(event) => {
              setNumber("position_on_deck_x", event.target.value);
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          />

          <TextField
            id="input-position-y"
            label="Deck position Y"
            margin="normal"
            value={editedEquipment?.position_on_deck_y ?? ""}
            onChange={(event) => {
              setNumber("position_on_deck_y", event.target.value);
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          />

          <TextField
            id="input-location"
            label="Location (optional)"
            margin="normal"
            value={editedEquipment?.location || ""}
            onChange={(event) => {
              setEditedEquipment({ ...editedEquipment, location: event.target.value });
            }}
            variant="standard"
            sx={{ flex: "0 0 48%" }}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          {globalError && (
            <Typography variant="body1" color="error">
              {`${globalError}`}
            </Typography>
          )}
          <Button
            disabled={
              !editedEquipment?.type ||
              !editedEquipment?.direction ||
              !editedEquipment?.station_name ||
              editedEquipment?.position_on_deck_x === undefined ||
              editedEquipment?.position_on_deck_x === "" ||
              editedEquipment?.position_on_deck_y === undefined ||
              editedEquipment?.position_on_deck_y === ""
            }
            onClick={onSubmitClick}
            variant="contained"
            sx={{ mt: 1 }}
          >
            {submitButtonTitle}
          </Button>
        </Box>
      </Container>
    </div>
  );
};
